import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../../layouts/AdminLayout";
import DataTableLayout from "../../../layouts/DataTableLayout";
import DataTable from "../../../components/common/DataTable";
import Button from "../../../components/ui/Button";
import { getMyAmenityBookings, updateBookingStatus } from "../../../services/amenityService";
import { toast } from "react-toastify";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import Modal from "../../../components/ui/Modal";
import Skeleton from "../../../components/ui/Skeleton";
import StatusBadge from "../../../components/ui/StatusBadge";

const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const ResidentMyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("calendar"); // "list" or "calendar"
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const navigate = useNavigate();

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const data = await getMyAmenityBookings();
      setBookings(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching my bookings:", err);
      toast.error("Failed to load your amenity bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await updateBookingStatus(id, status);
      toast.success(`Booking ${status.toLowerCase()} successfully`);
      fetchBookings();
    } catch (err) {
      toast.error(`Failed to update booking status`);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const columns = [
    {
      key: "amenity",
      label: "Amenity",
      render: (row) => (
        <div className="font-semibold text-gray-900">
          {row.amenity?.name || "-"}
        </div>
      )
    },
    {
      key: "schedule",
      label: "Schedule",
      render: (row) => (
        <div>
          <div className="text-sm font-medium">
            {row.booking_date ? new Date(row.booking_date).toLocaleDateString() : "-"}
          </div>
          <div className="text-xs text-gray-500">
            {row.start_time ? new Date(row.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}
            {" - "}
            {row.end_time ? new Date(row.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}
          </div>
        </div>
      )
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span
          className={`px-3 py-1 text-xs font-semibold rounded-full ${row.booking_status === "Approved"
            ? "bg-green-100 text-green-800"
            : row.booking_status === "Rejected"
              ? "bg-red-100 text-red-800"
              : "bg-yellow-100 text-yellow-800"
            }`}
        >
          {row.booking_status || "Pending"}
        </span>
      )
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="flex space-x-2">
          {["Pending", "Approved"].includes(row.booking_status) && (
            <Button
              variant="outline"
              className="!border-red-500 !text-red-600 hover:!bg-red-50 !py-1 !px-3 !text-xs"
              onClick={() => handleStatusUpdate(row.booking_id, "Cancelled")}
            >
              Cancel
            </Button>
          )}
        </div>
      )
    }
  ];

  // Map to calendar events
  const events = bookings.map(b => {
    let start = new Date(b.booking_date);
    if (b.start_time) {
      const st = new Date(b.start_time);
      start.setHours(st.getHours(), st.getMinutes(), 0, 0);
    }

    let end = new Date(b.booking_date);
    if (b.end_time) {
      const et = new Date(b.end_time);
      end.setHours(et.getHours(), et.getMinutes(), 0, 0);
    }

    return {
      id: b.booking_id,
      title: `${b.amenity?.name} (${b.booking_status})`,
      start,
      end,
      status: b.booking_status,
      originalBooking: b
    };
  });

  const eventStyleGetter = (event) => {
    let backgroundColor = "#fef08a"; // yellow-200
    let color = "#854d0e"; // yellow-800

    if (event.status === "Approved") {
      backgroundColor = "#bbf7d0"; // green-200
      color = "#166534"; // green-800
    } else if (event.status === "Rejected") {
      backgroundColor = "#fecaca"; // red-200
      color = "#991b1b"; // red-800
    }

    return {
      style: {
        backgroundColor,
        color,
        borderRadius: "4px",
        border: "none",
        fontWeight: "600",
        padding: "2px 4px",
        fontSize: "0.85em"
      }
    };
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  return (
    <AdminLayout title="My Bookings">
      <div className="flex justify-between items-center border-b border-gray-200" style={{ paddingBottom: '16px', margin: '24px 24px 24px 24px' }}>
        <div className="flex" style={{ gap: '12px', display: "flex", alignItems: "center" }}>
          <Button
            variant={viewMode === "calendar" ? "primary" : "outline"}
            onClick={() => setViewMode("calendar")}
          >
            Calendar View
          </Button>
          <Button
            variant={viewMode === "list" ? "primary" : "outline"}
            onClick={() => setViewMode("list")}
          >
            List View
          </Button>

          <Button variant="primary" onClick={() => navigate("/resident/amenities")}>
            Book Now
          </Button>

        </div>
        <div>

        </div>
      </div>

      {viewMode === "list" ? (
        <DataTableLayout title="My Bookings">
          {loading ? (
            <Skeleton variant="list" count={3} />
          ) : (
            <DataTable
              columns={columns}
              data={bookings}
              emptyMessage="You have not booked any amenities yet."
            />
          )}
        </DataTableLayout>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow-md mx-6 border border-gray-200 h-[650px] flex flex-col">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-800">Your Booking Calendar</h2>
            <p className="text-sm text-gray-500">View all your upcoming and past amenity reservations.</p>
          </div>
          <div className="flex-grow w-full h-full pb-10">
            {loading ? (
              <Skeleton variant="list" count={3} />
            ) : (
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%', minHeight: '500px' }}
                eventPropGetter={eventStyleGetter}
                onSelectEvent={handleEventClick}
                views={['month', 'week', 'day', 'agenda']}
                className="font-sans"
              />
            )}
          </div>
        </div>
      )}

      {/* Booking Details Modal */}
      <Modal
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        title="Booking Details"
      >
        {selectedEvent && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1e293b' }}>{selectedEvent.title}</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px', fontSize: '15px' }}>
                <strong style={{ color: '#64748b' }}>Status:</strong>
                <StatusBadge status={selectedEvent.status} label={selectedEvent.status} />

                <strong style={{ color: '#64748b' }}>Society:</strong>
                <span>{selectedEvent.originalBooking?.societies?.society_name || '-'}</span>

                <strong style={{ color: '#64748b' }}>Resident:</strong>
                {selectedEvent.originalBooking?.users ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedUser(selectedEvent.originalBooking.users)}
                    style={{
                      padding: 0,
                      height: 'auto',
                      minHeight: 'auto',
                      color: 'var(--color-dark-bg)',
                      textDecoration: 'underline',
                      textAlign: 'left',
                    }}
                  >
                    {selectedEvent.originalBooking.users.full_name}
                  </Button>
                ) : (
                  <span>-</span>
                )}

                <strong style={{ color: '#64748b' }}>Contact:</strong>
                <span>{selectedEvent.originalBooking?.users?.mobile_number || '-'}</span>

                <strong style={{ color: '#64748b' }}>Flat:</strong>
                <span>{selectedEvent.originalBooking?.flats?.flat_number || '-'}</span>

                <strong style={{ color: '#64748b' }}>Date:</strong>
                <span>{selectedEvent.start.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>

                <strong style={{ color: '#64748b' }}>Time:</strong>
                <span>
                  {selectedEvent.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {' - '}
                  {selectedEvent.end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px', gap: '8px' }}>
              {["Pending", "Approved"].includes(selectedEvent.status) && (
                <Button
                  variant="outline"
                  className="!border-red-500 !text-red-600 hover:!bg-red-50"
                  onClick={() => {
                    handleStatusUpdate(selectedEvent.id, "Cancelled");
                    setSelectedEvent(null);
                  }}
                >
                  Cancel Booking
                </Button>
              )}
              <Button variant="outline" onClick={() => setSelectedEvent(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Resident Details Modal */}
      <Modal
        isOpen={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        title="Resident Details"
      >
        {selectedUser && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px', fontSize: '15px' }}>
              <strong style={{ color: '#64748b' }}>Name:</strong>
              <span>{selectedUser.full_name || '-'}</span>

              <strong style={{ color: '#64748b' }}>Email:</strong>
              <span>{selectedUser.email_id || '-'}</span>

              <strong style={{ color: '#64748b' }}>Mobile:</strong>
              <span>{selectedUser.mobile_number || '-'}</span>

              <strong style={{ color: '#64748b' }}>Gender:</strong>
              <span>{selectedUser.gender || '-'}</span>

              <strong style={{ color: '#64748b' }}>User Type:</strong>
              <span>{selectedUser.system_role || selectedUser.primary_role || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
              <Button variant="outline" onClick={() => setSelectedUser(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default ResidentMyBookings;
